import { useCallback, useEffect, useMemo, useState } from 'react'
import { VehicleApi, VehicleProfile } from '@/shared/api'
import { useAuth } from '@/modules/auth'

export function useVehicles() {
  const { getAccessToken, isAuthenticated } = useAuth()
  const [vehicles, setVehicles] = useState<VehicleProfile[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Create vehicle API instance
  const vehicleApi = useMemo(() => new VehicleApi(getAccessToken), [getAccessToken])

  // Load vehicles
  const loadVehicles = useCallback(async () => {
    if (!isAuthenticated) return

    setIsLoading(true)
    setError(null)
    
    try {
      const vehicleData = await vehicleApi.getVehicles()
      setVehicles(vehicleData)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load vehicles'
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }, [vehicleApi, isAuthenticated])

  // Create vehicle
  const createVehicle = useCallback(async (vehicleData: any) => {
    try {
      const newVehicle = await vehicleApi.createVehicle(vehicleData)
      setVehicles(prev => [newVehicle, ...prev])
      return newVehicle
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create vehicle'
      setError(errorMessage)
      throw error
    }
  }, [vehicleApi])

  // Update vehicle
  const updateVehicle = useCallback(async (vehicleId: string, vehicleData: any) => {
    try {
      const updatedVehicle = await vehicleApi.updateVehicle(vehicleId, vehicleData)
      setVehicles(prev => prev.map(v => v.id === vehicleId ? updatedVehicle : v))
      return updatedVehicle
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update vehicle'
      setError(errorMessage)
      throw error
    }
  }, [vehicleApi])

  // Delete vehicle
  const deleteVehicle = useCallback(async (vehicleId: string) => {
    try {
      await vehicleApi.deleteVehicle(vehicleId)
      setVehicles(prev => prev.filter(v => v.id !== vehicleId))
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete vehicle'
      setError(errorMessage)
      throw error
    }
  }, [vehicleApi])

  // Set primary vehicle
  const setPrimaryVehicle = useCallback(async (vehicleId: string) => {
    try {
      const updatedVehicle = await vehicleApi.setPrimaryVehicle(vehicleId)
      setVehicles(prev => prev.map(v => ({
        ...v,
        is_primary: v.id === vehicleId
      })))
      return updatedVehicle
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to set primary vehicle'
      setError(errorMessage)
      throw error
    }
  }, [vehicleApi])

  // Get primary vehicle
  const primaryVehicle = useMemo(() => {
    return vehicles.find(v => v.is_primary) || vehicles[0] || null
  }, [vehicles])

  // Clear error
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  // Load vehicles on mount
  useEffect(() => {
    if (isAuthenticated) {
      loadVehicles()
    }
  }, [isAuthenticated, loadVehicles])

  return {
    // State
    vehicles,
    primaryVehicle,
    isLoading,
    error,
    
    // Actions
    loadVehicles,
    createVehicle,
    updateVehicle,
    deleteVehicle,
    setPrimaryVehicle,
    clearError
  }
}
